import type { Matches } from 'common/hooks/media';
import { useMatchesViewport } from 'common/hooks/media';
import { isClient } from 'common/utils/isClient';
import type { SliderConfig } from '../types';

const doesValueExist = (value: number[], index: number): value is Required<number[]> =>
  index in value;

const getClosestValue = (value: number[], index: number) => {
  for (let i = index; i <= value.length; i--) {
    if (doesValueExist(value, i)) {
      return value[i];
    }
  }

  return value[0];
};

const getClosestConfig = ({ width, gap }: SliderConfig, index: number) => {
  const closestWidth = getClosestValue(width, index);
  const closestGap = getClosestValue(gap, index);

  return [closestWidth, closestGap];
};

const getSlideSize = ({
  matches: { matchesTablet, matchesDesktop, matchesLargeDesktop },
  ...config
}: SliderConfig & { matches: Matches }) => {
  switch (true) {
    case !isClient():
      /**
       * On SSR prefer desktop sizes. This improves Calibre scores 🪄
       * {@link https://home24.atlassian.net/browse/FP-3432}.
       */
      return getClosestConfig(config, 2);
    case matchesLargeDesktop:
      return getClosestConfig(config, 3);
    case matchesDesktop:
      return getClosestConfig(config, 2);
    case matchesTablet:
      return getClosestConfig(config, 1);
    default:
      return [config.width[0], config.gap[0]];
  }
};

export const useSlideSize = (config: SliderConfig) => {
  const { matches } = useMatchesViewport();
  const { matchesTablet } = matches;
  const [slideWidth, slideGap] = getSlideSize({ ...config, matches });

  return {
    matchesTablet,
    slideWidth,
    slideGap,
  };
};
